/* ----------------------------------------------------------------------------
   M0 · Courier Code Deep Dive  (route /supply/codes/:code)
   React port of renderCodeDeepDive(): hierarchy breadcrumb header, vertical
   section menu (sidenav), 5 sections — Overview & Setup · Service Types ·
   Agreements · Commercials · Tags.
   ---------------------------------------------------------------------------- */
import React, {useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {Box, Stack, Typography, Button, Chip} from '@mui/material'
import {alpha} from '@mui/material/styles'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import CheckIcon from '@mui/icons-material/Check'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import PageTitle from '../../../components/PageTitle'
import {Badge, KpiStrip, useToast, TableSkeleton, LoadErrorPanel, HERO_BTN, HERO_BTN_SOLID, LogicalName} from '../components'
import {initials, fmtDate, inr} from '../components'
import {SERVICE_TYPES_BY_CODE} from '../dummyData'
import {useSearchCouriersQuery, useAgreementTimelineQuery, useListParentsQuery, useUpdateCourierMutation} from '../../../redux/supplyApiSlice'
import {pick, normalizeCourier, itemsForCode, codeAgreementInfo} from '../m1-codes/codeData'
import CodeFormDrawer from '../m1-codes/CodeFormDrawer'

const NO_CODES = [] /* stable empty ref for the drawer's duplicate checks (the code itself is locked on edit) */
const NO_PARENTS = []

const ICO = {fontSize: 16}

const AGREEMENT_TYPE_LABEL = {
  MSA: 'Master Agreement (MSA)',
  COMMERCIAL_ADDENDUM: 'Commercial Addendum',
  OPERATIONAL_ADDENDUM: 'Operational Addendum',
}

/* coerce any value to something safe to render as text (backend sometimes returns
   objects, e.g. sla = {id, sla, startDate, endDate, active}) */
const asText = (v) => {
  if (v == null) return ''
  if (typeof v === 'string' || typeof v === 'number') return String(v)
  if (typeof v === 'object') {
    const inner = v.sla ?? v.value ?? v.name ?? v.label
    return typeof inner === 'string' || typeof inner === 'number' ? String(inner) : ''
  }
  return ''
}

/* backend courier doc → the flat shape the deep-dive sections expect */
function adaptCode(doc) {
  const base = normalizeCourier(doc)
  const tagsRaw = pick(doc, 'tags')
  return {
    ...base,
    parent: asText(base.parent),
    vertical: asText(base.vertical) || 'D2C',
    prozoId: asText(base.prozoId),
    email: asText(base.email),
    sla: asText(pick(doc, 'sla', 'slaDays', 'sla_days')),
    tags: Array.isArray(tagsRaw) ? tagsRaw.filter((t) => typeof t === 'string') : [],
    lastActivated: base.lastModified || pick(doc, 'createdDate', 'created_date') || null,
    lastActivatedBy: asText(pick(doc, 'modifiedByUser', 'modified_by_user', 'createdByUser', 'created_by_user')),
  }
}

/* live agreement-timeline items applying to this code → the agreements section's shape */
function adaptAgreements(timeline, c) {
  return itemsForCode(timeline, c)
    .map((it) => (it && it.agreement) || {})
    .map((a) => {
      const rawType = pick(a, 'type') || ''
      return {
        id: pick(a, 'id') || pick(a, 'title') || rawType,
        type: AGREEMENT_TYPE_LABEL[rawType] || rawType,
        addendumNo: pick(a, 'addendumNo', 'addendum_no'),
        stage: pick(a, 'stage') === 'SIGNED' ? 2 : 1,
        appliesToAll: pick(a, 'appliesToAll', 'applies_to_all'),
        linkedCodes: pick(a, 'linkedCpAccountCodes', 'linked_cp_account_codes') || [],
        effectiveFrom: pick(a, 'effectiveFrom', 'effective_from'),
        effectiveTo: pick(a, 'effectiveTo', 'effective_to'),
        title: pick(a, 'title') || '',
        uploadedBy: pick(a, 'createdByUser', 'created_by_user') || ((pick(a, 'documents') || [])[0] || {}).uploadedBy || '',
        uploadedAt: pick(a, 'createdDate', 'created_date') || '',
      }
    })
    .sort((x, y) => new Date(y.effectiveFrom || 0) - new Date(x.effectiveFrom || 0))
}

/* ── 5 section ids / labels / icons ── */
const SECS = [
  {id: 'sec-overview', label: 'Overview & Setup', icon: <InfoOutlinedIcon style={ICO} />},
  {
    id: 'sec-services',
    label: 'Service Types',
    icon: <SettingsInputComponentOutlinedIcon style={ICO} />,
    cntKey: 'services',
  },
  {
    id: 'sec-agreements',
    label: 'Agreements',
    icon: <DescriptionOutlinedIcon style={ICO} />,
    cntKey: 'agreements',
  },
  {
    id: 'sec-commercials',
    label: 'Commercials',
    icon: <AttachMoneyOutlinedIcon style={ICO} />,
    cntKey: 'commercials',
  },
  {id: 'sec-tags', label: 'Tags', icon: <BoltOutlinedIcon style={ICO} />},
]

/* ── Section 1: Overview & Setup ── */
function OverviewSection({c, p, codeAgrs, partnerName, agrBadge}) {
  const setupLine = c.lastActivated ? (
    <>
      {fmtDate(c.lastActivated)}
      {c.lastActivatedBy ? (
        <>
          {' '}
          &nbsp;·&nbsp; by <b>{c.lastActivatedBy}</b>
        </>
      ) : null}
    </>
  ) : (
    <span className='mini-pill mp-amber'>Not yet activated — setup in progress</span>
  )

  const fields = [
    {label: 'Courier code', value: <Box component='span' sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 13}}>{c.code}</Box>},
    {label: 'Display name', value: c.display || '—'},
    {label: 'Parent courier partner', value: <Box component='a' href={'/supply/partners/' + c.parentId} sx={{color: 'primary.main', textDecoration: 'none', fontWeight: 600}}>{partnerName}</Box>},
    {label: 'Freight vertical', value: <Chip size='small' label={c.vertical} sx={{height: 20, fontSize: 11, fontWeight: 700, bgcolor: (th) => alpha(th.palette.primary.main, 0.12), color: 'primary.main'}} />},
    {label: 'Prozo ID', value: <Box component='span' sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 13}}>{c.prozoId}</Box>},
    {label: 'Logical name', value: <LogicalName value={c.logicalName} priority={c.priority} size='lg' />},
    {label: 'Priority', value: c.priority != null ? c.priority + ' (1 = picked first among codes sharing the logical name)' : <span className='muted'>Not set — sorts last</span>},
    {label: 'Delivery SLA', value: c.sla || '—'},
    {label: 'Operational email', value: c.email ? <Box component='a' href={'mailto:' + c.email} sx={{color: 'primary.main', textDecoration: 'none'}}>{c.email}</Box> : '—'},
    {label: 'Agreement status', value: <Badge status={agrBadge.key} title={agrBadge.tip} />},
    {label: 'Setup completed', value: setupLine, full: true},
  ]
  return (
    <Box sx={{border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', p: 2.5}}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{mb: 2}}>
        <Typography sx={{fontWeight: 800, fontSize: 15.5, color: 'text.primary'}}>Overview &amp; Setup</Typography>
        <Badge status={c.status} />
      </Stack>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1.5}}>
        {fields.map((f, i) => (
          <Box key={i} sx={{gridColumn: f.full ? {sm: '1 / -1'} : 'auto', p: 1.5, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: (th) => alpha(th.palette.text.primary, 0.015)}}>
            <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary'}}>{f.label}</Typography>
            <Box sx={{fontSize: 13.5, fontWeight: 500, color: 'text.primary', mt: 0.25}}>{f.value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

/* ── Section 2: Service Types ── */
function ServiceTypesSection({c}) {
  const stCodes = c.serviceTypes || []
  return (
    <div className='sidenav-sec' id='sec-services'>
      <div className='card'>
        <div className='card-hd'>
          <h3>Service Types</h3>
          <span className='muted' style={{fontSize: 12}}>
            {stCodes.length} configured at this courier-code level
          </span>
        </div>
        <div className='tbl-wrap'>
          <table className='tbl'>
            <thead>
              <tr>
                <th>Service Type</th>
                <th>Code</th>
                <th>Category</th>
                <th>Weight band</th>
                <th style={{textAlign: 'center'}}>COD</th>
              </tr>
            </thead>
            <tbody>
              {stCodes.length ? (
                stCodes.map((stc) => {
                  const st = SERVICE_TYPES_BY_CODE[stc]
                  const codPill =
                    st ? (
                      st.cod === 'Yes' ? (
                        <span className='mini-pill mp-green'>Yes</span>
                      ) : st.cod === 'No' ? (
                        <span className='mini-pill mp-grey'>No</span>
                      ) : (
                        <span className='mini-pill mp-grey'>{st.cod}</span>
                      )
                    ) : (
                      '—'
                    )
                  return (
                    <tr key={stc}>
                      <td>
                        <b>{st ? st.name : stc}</b>
                        {st && st.note ? (
                          <div className='muted' style={{fontSize: 11, marginTop: 2}}>
                            {st.note}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className='tag tag-mono'>{stc}</span>
                      </td>
                      <td>{st ? st.cat : '—'}</td>
                      <td>{st ? st.wt : '—'}</td>
                      <td style={{textAlign: 'center'}}>{codPill}</td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={5} className='muted' style={{padding: 14}}>
                    No service types configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ── Section 3: Agreements ── */
function AgreementsSection({c, codeAgrs, partnerName, toast}) {
  return (
    <div className='sidenav-sec' id='sec-agreements'>
      <div className='card card-pad'>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 14,
          }}
        >
          <h3 style={{margin: 0}}>Agreements mapped to this code</h3>
          {/* Admin: always show add button (single-admin persona) */}
          <button
            className='btn btn-sm btn-primary'
            style={{marginLeft: 'auto'}}
            onClick={() => toast('Upload scoped to ' + c.code + ' — agreement drawer (coming soon)', 'info')}
          >
            <AddOutlinedIcon style={{fontSize: 14}} />
            Add agreement for this code
          </button>
        </div>

        {codeAgrs.length ? (
          <>
            <div className='timeline'>
              {codeAgrs.map((a) => {
                const cls = a.stage === 2 ? 'green' : 'amber'
                const head =
                  a.type === 'Commercial Addendum' ? 'Addendum #' + a.addendumNo : a.type
                const scope = a.appliesToAll ? (
                  <span className='mini-pill mp-grey'>Parent-wide</span>
                ) : (
                  <span className='mini-pill mp-green'>Code-specific</span>
                )
                const applies = a.appliesToAll
                  ? 'Parent-wide — applies to all ' + partnerName + ' codes'
                  : 'Specific codes: ' + (a.linkedCodes || []).join(', ')
                return (
                  <div className={'tl-item ' + cls} key={a.id}>
                    <div className='tl-card'>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 8,
                        }}
                      >
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                          <b>{head}</b>
                          <Badge
                            status={a.stage === 2 ? 'signed' : 'pending'}
                            label={a.stage === 2 ? 'Stage 2: Signed' : 'Stage 1: Email received'}
                          />
                          {scope}
                        </div>
                        <span className='muted' style={{fontSize: 12}}>
                          Effective {fmtDate(a.effectiveFrom)}
                          {a.effectiveTo ? ' → ' + fmtDate(a.effectiveTo) : ' · open-ended'}
                        </span>
                      </div>
                      <div style={{fontSize: 13.5, marginTop: 8, fontWeight: 500}}>{a.title}</div>
                      <div className='tl-meta'>
                        {applies} · Uploaded by {a.uploadedBy} on {fmtDate(a.uploadedAt)}
                      </div>
                      <div style={{marginTop: 10, display: 'flex', gap: 8}}>
                        <button className='btn btn-sm'>
                          <DownloadOutlinedIcon style={{fontSize: 14}} />
                          Download
                        </button>
                        {a.stage === 1 ? (
                          <button className='btn btn-sm'>
                            <CheckIcon style={{fontSize: 14}} />
                            Update to Stage 2
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {codeAgrs.length > 1 ? (
              <div className='warnbox info' style={{marginTop: 12}}>
                <InfoOutlinedIcon style={{fontSize: 16}} />
                <div>
                  <b>{codeAgrs.length} agreements</b> map to this code. The commercials currently
                  governing it come from the latest <b>signed</b> agreement — see Commercials below.
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className='warnbox err' style={{margin: 0}}>
            <WarningAmberOutlinedIcon style={{fontSize: 16}} />
            <div>
              No agreement on file for this code. Use &ldquo;Add agreement for this code&rdquo; to
              upload one.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Section 4: Commercials ── */
function CommercialsSection({c, rcRows, navigate}) {
  return (
    <div className='sidenav-sec' id='sec-commercials'>
      <div className='card'>
        <div className='card-hd'>
          <h3>Commercials</h3>
          <span className='muted' style={{fontSize: 12}}>
            rate card &amp; governing agreement for this code
          </span>
        </div>
        <div className='card-pad'>
          {rcRows.length ? (
            <>
              <div className='tbl-wrap'>
                <table className='tbl'>
                  <thead>
                    <tr>
                      <th>Zone</th>
                      <th>Weight slab</th>
                      <th style={{textAlign: 'right'}}>Rate</th>
                      <th>Effective</th>
                      <th>Governing agreement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rcRows.map((r, i) => (
                      <tr key={i}>
                        <td>{r.zone}</td>
                        <td>{r.slab}</td>
                        <td style={{textAlign: 'right'}}>
                          <b>{inr(r.rate)}</b>
                        </td>
                        <td>{fmtDate(r.effectiveFrom)}</td>
                        <td>{r.linkedAgreement || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{marginTop: 12}}>
                <button
                  className='btn btn-sm'
                  onClick={() =>
                    navigate('/supply/partners/update?code=' + encodeURIComponent(c.code))
                  }
                >
                  <OpenInNewOutlinedIcon style={{fontSize: 14}} />
                  Update Rates &amp; TAT
                </button>
              </div>
            </>
          ) : (
            <div className='empty'>
              <div className='ic'>
                <AttachMoneyOutlinedIcon style={{fontSize: 30}} />
              </div>
              <h3>No rate card captured</h3>
              <p>No commercials are loaded for this code yet.</p>
              <button
                className='btn btn-sm'
                style={{marginTop: 8}}
                onClick={() =>
                  navigate('/supply/partners/update?code=' + encodeURIComponent(c.code))
                }
              >
                Update Rates &amp; TAT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Section 5: Tags ── */
function TagsSection({c, p, partnerName}) {
  const codeTags = c.tags || []
  const partnerTags = (p && p.tags) || []

  function TagChips({arr}) {
    if (!arr || !arr.length) return <span className='muted'>—</span>
    return (
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
        {arr.map((t) => (
          <span className='tag' key={t}>
            <BoltOutlinedIcon style={{fontSize: 11, verticalAlign: '-2px'}} />
            {t}
          </span>
        ))}
      </div>
    )
  }

  return (
    <div className='sidenav-sec' id='sec-tags'>
      <div className='card'>
        <div className='card-hd'>
          <h3>Tags</h3>
        </div>
        <div className='card-pad'>
          <div className='seclabel' style={{marginBottom: 8}}>
            Courier-code tags
          </div>
          <div style={{marginBottom: 18}}>
            <TagChips arr={codeTags} />
          </div>
          <div className='seclabel' style={{marginBottom: 8}}>
            Parent courier-partner tags — {partnerName}
          </div>
          <TagChips arr={partnerTags} />
        </div>
      </div>
    </div>
  )
}

/* ════════════════════════════ Main Component ════════════════════════════ */
export default function CourierCodeDeepDive() {
  const {code: codeStr} = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [activeSection, setActiveSection] = useState('sec-overview')

  /* resolve the code from the backend (no dedicated GET — search by cp_account_code) */
  const {data: searchData, isFetching: codeLoading, isError: codeError, error: codeErrObj, refetch} =
    useSearchCouriersQuery({offset: 0, limit: 1, andfilter: [{field: 'cp_account_code', operator: 'eq', value: codeStr}]})
  const foundDoc = (Array.isArray(searchData) ? searchData : (searchData && searchData.data) || [])[0] || null
  /* memoized: the edit drawer resets its form whenever `existing` changes identity, so a fresh object
     per render would wipe the admin's typing on every re-render while the drawer is open */
  const c = useMemo(() => (foundDoc ? adaptCode(foundDoc) : null), [foundDoc])

  /* the parent's live agreement timeline (same shape the codes list already uses) */
  const {data: tlData} = useAgreementTimelineQuery(c ? c.parentId : undefined, {skip: !c || !c.parentId})
  const timeline = Array.isArray(tlData) ? tlData : []

  /* ── Edit in place: same Add/Edit drawer and PATCH the Courier Codes list uses; refetch on save so the
     hero, logical name and Overview rows reflect the change without leaving the page ── */
  const [editOpen, setEditOpen] = useState(false)
  const {data: parentListRaw} = useListParentsQuery()
  const [updateCourier] = useUpdateCourierMutation()
  const commitCode = ({existing, doc}) =>
    updateCourier({id: existing.id, ...doc})
      .unwrap()
      .then(() => {
        toast(existing.code + ' updated.', 'success')
        setEditOpen(false)
        refetch()
      })
  /* rejection handled in the drawer (stays open, shows backend error) */

  if (codeLoading && !c) {
    return (
      <>
        <PageTitle>{'Courier Code'}</PageTitle>
        <Box sx={{mt: 2}}>
          <TableSkeleton rows={5} />
        </Box>
      </>
    )
  }

  if (codeError) {
    return (
      <>
        <PageTitle>{'Courier Code'}</PageTitle>
        <Box sx={{mt: 2}}>
          <LoadErrorPanel
            message={(codeErrObj && codeErrObj.message) || 'Something went wrong loading this code.'}
            onRetry={refetch}
            title='Couldn’t load this courier code'
          />
        </Box>
      </>
    )
  }

  /* ── Code not found ── */
  if (!c) {
    return (
      <>
        <PageTitle>{'Courier Code Not Found'}</PageTitle>
        <Box sx={{display: 'flex', justifyContent: 'center', mt: {xs: 4, md: 8}}}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 520,
              textAlign: 'center',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(15,23,42,.05)',
              p: {xs: 4, md: 6},
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '18px',
                mx: 'auto',
                mb: 2,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchOutlinedIcon sx={{fontSize: 30}} />
            </Box>
            <Typography sx={{fontWeight: 700, fontSize: 18, color: 'text.primary'}}>Courier code not found</Typography>
            <Box
              sx={{
                display: 'inline-block',
                mt: 1.25,
                px: 1.5,
                py: 0.5,
                borderRadius: '8px',
                bgcolor: 'background.neutral',
                border: 1,
                borderColor: 'divider',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12.5,
                color: 'text.secondary',
                wordBreak: 'break-all',
              }}
            >
              {codeStr}
            </Box>
            <Typography sx={{color: 'text.secondary', fontSize: 13.5, mt: 1.5, maxWidth: 380, mx: 'auto', lineHeight: 1.5}}>
              We couldn’t find a courier code with this ID. It may have been removed, or the link is out of date.
            </Typography>
            <Button
              variant='contained'
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/supply/courier-codes'))}
              sx={{mt: 3, borderRadius: '12px', px: 3}}
            >
              Go Back
            </Button>
          </Box>
        </Box>
      </>
    )
  }

  const p = null /* parent details aren't separately fetched; header uses the code's parent name */
  const partnerName = c.parent || c.parentId

  /* agreements mapped to this code, derived from the live agreement timeline */
  const codeAgrs = adaptAgreements(timeline, c)
  const agrBadge = codeAgreementInfo(timeline, c)

  /* Commercials/rate-card wiring is pending confirmation of the parentRateCards shape on
     live data — the section shows its graceful empty + the "Commercials" link for now. */
  const rcRows = []

  const codeTags = c.tags || []
  const partnerTags = (p && p.tags) || []
  const allTags = [...codeTags, ...partnerTags]

  /* counts for sidenav pills */
  const cntMap = {
    services: (c.serviceTypes || []).length,
    agreements: codeAgrs.length,
    commercials: rcRows.length,
  }

  return (
    <>
      <PageTitle>{(c.display || c.code) + ' — Courier Code'}</PageTitle>

      {/* hero — dark banner matching the rest of Courier Setup */}
      <Box sx={{position: 'relative', overflow: 'hidden', borderRadius: '12px', mb: 3, background: 'linear-gradient(135deg, #0d1b2a 0%, #1b3a5c 60%, #0d2137 100%)'}}>
        <Box sx={{position: 'absolute', right: -60, top: -60, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)', pointerEvents: 'none'}} />
        <Box sx={{position: 'relative', p: {xs: 2.5, md: 3.5}}}>
          {/* breadcrumb */}
          <Stack direction='row' alignItems='center' spacing={0.5} flexWrap='wrap' useFlexGap sx={{mb: 1.5}}>
            <Box component='button' onClick={() => navigate('/supply/partners')} sx={{border: 'none', bgcolor: 'transparent', p: 0, font: 'inherit', cursor: 'pointer', color: '#76B0F1', fontWeight: 600, fontSize: 12.5}}>Courier Partners</Box>
            <ChevronRightOutlinedIcon sx={{fontSize: 14, color: 'rgba(255,255,255,.4)'}} />
            <Box component='button' onClick={() => navigate('/supply/partners/' + c.parentId)} sx={{border: 'none', bgcolor: 'transparent', p: 0, font: 'inherit', cursor: 'pointer', color: '#76B0F1', fontWeight: 600, fontSize: 12.5}}>{partnerName}</Box>
            <ChevronRightOutlinedIcon sx={{fontSize: 14, color: 'rgba(255,255,255,.4)'}} />
            <Typography component='span' sx={{fontSize: 12.5, fontWeight: 700, color: '#fff'}}>{c.display || c.code}</Typography>
          </Stack>

          <Stack direction={{xs: 'column', md: 'row'}} spacing={2.5} justifyContent='space-between' alignItems={{md: 'flex-start'}}>
            {/* identity */}
            <Stack direction='row' spacing={2.25} alignItems='flex-start' sx={{minWidth: 0}}>
              <Box sx={{width: 60, height: 60, borderRadius: '17px', flexShrink: 0, background: 'linear-gradient(135deg, #2065D1, #1B4FA8)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, boxShadow: '0 6px 16px rgba(32,101,209,.45)'}}>
                {initials(partnerName)}
              </Box>
              <Box sx={{minWidth: 0}}>
                <Stack direction='row' spacing={1.25} alignItems='center' flexWrap='wrap' useFlexGap>
                  <Typography sx={{fontSize: 23, fontWeight: 800, letterSpacing: '-.3px', color: '#fff'}}>{c.display || c.code}</Typography>
                  <Badge status={c.status} />
                  <Badge status={agrBadge.key} title={agrBadge.tip} />
                </Stack>
                {c.code !== (c.display || c.code) ? (
                  <Typography sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'rgba(255,255,255,.6)', mt: 0.5}}>{c.code}</Typography>
                ) : null}
                <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap sx={{mt: 1}}>
                  <Chip size='small' onClick={() => navigate('/supply/partners/' + c.parentId)} label={'Parent · ' + partnerName} sx={{height: 22, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', color: '#fff', bgcolor: 'rgba(255,255,255,.12)', border: 1, borderColor: 'rgba(255,255,255,.2)'}} />
                  <Chip size='small' label={c.vertical} sx={{height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'rgba(118,176,241,.22)', color: '#fff'}} />
                  <Chip size='small' label={'prozoId ' + c.prozoId} sx={{height: 22, fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: '#fff', bgcolor: 'rgba(255,255,255,.12)', border: 1, borderColor: 'rgba(255,255,255,.2)'}} />
                </Stack>
                {/* Courier Code Display — own row with a label so the value reads as "the logical name", not a stray chip */}
                {c.logicalName ? (
                  <Stack direction='row' spacing={1} alignItems='center' sx={{mt: 1}}>
                    <Chip size='small' label={c.logicalName} sx={{height: 22, fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: '#fff', bgcolor: 'rgba(129,140,248,.35)', border: 1, borderColor: 'rgba(165,180,252,.5)'}} />
                    {c.priority != null ? (
                      <Chip size='small' label={'Priority ' + c.priority} sx={{height: 22, fontSize: 11, fontWeight: 700, color: '#fff', bgcolor: 'rgba(255,255,255,.12)', border: 1, borderColor: 'rgba(255,255,255,.2)'}} />
                    ) : null}
                  </Stack>
                ) : null}
                {allTags.length ? (
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{mt: 1}}>
                    {allTags.map((t) => (
                      <Chip key={t} size='small' icon={<BoltOutlinedIcon style={{fontSize: 12, color: 'rgba(255,255,255,.7)'}} />} label={t} sx={{height: 20, fontSize: 10.5, color: 'rgba(255,255,255,.85)', bgcolor: 'rgba(255,255,255,.08)', '& .MuiChip-icon': {ml: 0.5}}} />
                    ))}
                  </Stack>
                ) : null}
              </Box>
            </Stack>
            {/* actions */}
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{flexShrink: 0}}>
              <Button variant='outlined' startIcon={<EditOutlinedIcon style={{fontSize: 16}} />} onClick={() => setEditOpen(true)} sx={HERO_BTN}>
                Edit
              </Button>
              <Button variant='outlined' startIcon={<VisibilityOutlinedIcon style={{fontSize: 16}} />} onClick={() => navigate('/supply/partners/' + c.parentId)} sx={HERO_BTN}>
                Parent 360
              </Button>
              <Button variant='contained' disableElevation startIcon={<AttachMoneyOutlinedIcon style={{fontSize: 16}} />} onClick={() => navigate('/supply/partners/update?code=' + encodeURIComponent(c.code))} sx={HERO_BTN_SOLID}>
                Rates &amp; TAT
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Edit Courier Code drawer (same component as the Courier Codes list) */}
      <CodeFormDrawer
        open={editOpen}
        existing={c}
        codes={NO_CODES}
        parents={parentListRaw || NO_PARENTS}
        onClose={() => setEditOpen(false)}
        onCommit={commitCode}
        toast={toast}
      />

      {/* ── KPI strip ── */}
      <KpiStrip
        cards={[
          {
            label: 'Setup Completed',
            value: c.lastActivated ? fmtDate(c.lastActivated) : 'In progress',
            icon: <CalendarTodayOutlinedIcon style={ICO} />,
            accent: '#2065D1',
          },
          {
            label: 'Service Types',
            value: (c.serviceTypes || []).length,
            icon: <SettingsInputComponentOutlinedIcon style={ICO} />,
            accent: '#2065D1',
            onClick: () => setActiveSection('sec-services'),
          },
          {
            label: 'Agreements Mapped',
            value: codeAgrs.length,
            icon: <DescriptionOutlinedIcon style={ICO} />,
            accent: '#2E7D32',
            onClick: () => setActiveSection('sec-agreements'),
          },
          {
            label: 'Rate-card Rows',
            value: rcRows.length,
            icon: <AttachMoneyOutlinedIcon style={ICO} />,
            accent: '#F57C00',
            onClick: () => setActiveSection('sec-commercials'),
          },
        ]}
      />

      {/* ── section layout ── */}
      <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 2, alignItems: 'flex-start'}}>
        <Box
          component='nav'
          sx={{
            width: {xs: '100%', md: 250},
            flexShrink: 0,
            position: {md: 'sticky'},
            top: {md: 16},
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: '0 1px 2px rgba(15,23,42,.04)',
            p: 1,
            display: 'flex',
            flexDirection: {xs: 'row', md: 'column'},
            gap: 0.5,
            overflowX: {xs: 'auto', md: 'visible'},
          }}
        >
          {SECS.map((s) => {
            const cnt = s.cntKey != null ? cntMap[s.cntKey] : null
            const active = activeSection === s.id
            return (
              <Box
                key={s.id}
                component='button'
                onClick={() => setActiveSection(s.id)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: 2.5,
                  px: 1.25,
                  py: 1,
                  font: 'inherit',
                  fontSize: 13.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? 'primary.dark' : 'text.secondary',
                  whiteSpace: 'nowrap',
                  transition: 'all .14s',
                  bgcolor: active ? (th) => alpha(th.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': {bgcolor: active ? (th) => alpha(th.palette.primary.main, 0.1) : 'action.hover', color: active ? 'primary.dark' : 'text.primary'},
                }}
              >
                {active ? <Box sx={{position: 'absolute', left: 0, top: 9, bottom: 9, width: 3, borderRadius: 99, bgcolor: 'primary.main'}} /> : null}
                <Box sx={{width: 30, height: 30, borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: active ? 'primary.main' : 'action.hover', color: active ? '#fff' : 'text.secondary', transition: 'all .14s'}}>
                  {s.icon}
                </Box>
                <Box component='span' sx={{flex: 1, textAlign: 'left'}}>{s.label}</Box>
                {cnt != null ? (
                  <Box component='span' sx={{fontSize: 11, fontWeight: 700, bgcolor: active ? 'primary.main' : 'action.hover', color: active ? '#fff' : 'text.secondary', borderRadius: 99, px: 0.9, py: 0.1, minWidth: 20, textAlign: 'center'}}>
                    {cnt}
                  </Box>
                ) : null}
              </Box>
            )
          })}
        </Box>

        <Box sx={{flex: 1, minWidth: 0, width: '100%'}}>
          {activeSection === 'sec-overview' ? (
            <OverviewSection c={c} p={p} codeAgrs={codeAgrs} partnerName={partnerName} agrBadge={agrBadge} />
          ) : null}
          {activeSection === 'sec-services' ? <ServiceTypesSection c={c} /> : null}
          {activeSection === 'sec-agreements' ? (
            <AgreementsSection c={c} codeAgrs={codeAgrs} partnerName={partnerName} toast={toast} />
          ) : null}
          {activeSection === 'sec-commercials' ? (
            <CommercialsSection c={c} rcRows={rcRows} navigate={navigate} />
          ) : null}
          {activeSection === 'sec-tags' ? <TagsSection c={c} p={p} partnerName={partnerName} /> : null}
        </Box>
      </Box>
    </>
  )
}
